/* eslint-disable complexity */
import { Box } from '@mui/material';
import { useMemo } from 'react';

import { AbgTypography, formatMoney, TickIcon } from '@abg/kit-base';
import { VehiclePriceTypeEnum } from '@abg/shared/constants';

import { useAncillariesDialogContext } from '../../../../organisms/Ancillaries/contexts';
import { useTranslationContext } from '../../../../shared/context';
import { AncillariesI18nKeys } from '../../../../shared/types';
import { bookingFlowStoreSelector } from '../../../../store';
import { RateTermInfoItem } from '../RateTermInfoItem';
import { RateTermItem } from '../RateTermItem';
import { getLimitedMilesText } from '../utils/limited-miles-text/limited-miles-text';

import { RateTermsNotesProps } from './rate-terms-notes.types';
import {
  rateTermsNotesWrapperStyling,
  specialRateTermsIconStyling,
  specialRateTermsNotesStyling,
} from './RateTerms.styling';

export const RateTermsNotes = ({
  rateTerms,
  currency = 'USD',
  isPayNowChecked,
}: RateTermsNotesProps) => {
  const { t, i18nKeys } = useTranslationContext<AncillariesI18nKeys>();

  const context = useAncillariesDialogContext();
  const prices = bookingFlowStoreSelector.use.prices();
  const priceType = bookingFlowStoreSelector.use.priceType();
  // When isPayNowChecked is explicitly provided (e.g. from PreCheck flow),
  // use it as the definitive signal. Only fall back to booking-flow store's
  // priceType when the prop is undefined (normal checkout flow).
  const payLater =
    isPayNowChecked === undefined
      ? priceType === VehiclePriceTypeEnum.VEHICLE_LEISURE_PAY_LATER ||
        priceType === VehiclePriceTypeEnum.MEMBER_PAY_LATER ||
        priceType === VehiclePriceTypeEnum.VEHICLE_CORPORATE_PAY_LATER
      : !isPayNowChecked;
  const price = prices?.find((price) => price.priceType === priceType);
  const isCouponApplied = price?.coupon?.applied;

  const isConditionalCharges = rateTerms?.conditionalCharges;
  const isUnlimitedMilage = rateTerms?.unlimitedMilage;
  const isFreeCancellation = rateTerms?.freeCancellation || payLater;
  const periodFreeMiles = rateTerms?.periodFreeMiles;
  const limitedMilesText = useMemo(
    () => getLimitedMilesText(t, currency, periodFreeMiles),
    [t, currency, periodFreeMiles],
  );

  // TODO: Consider to preserve this conditions after BE integration
  // const isPrepay = prepayProcessIndicator || prepayOpted;
  // const notAUOrNZ =
  //   rentalLocationCountry !== 'AU' && rentalLocationCountry !== 'NZ';
  // const isAUOrNZ =
  //   rentalLocationCountry === 'AU' || rentalLocationCountry === 'NZ';

  return (
    <>
      <Box
        component="ul"
        sx={rateTermsNotesWrapperStyling}
        data-testid="rate-terms-notes-ul"
        fontSize={'inherit'}
      >
        {isConditionalCharges && (
          <RateTermItem>{t(i18nKeys.rateTermsInfoDisclaimer)}</RateTermItem>
        )}
        <RateTermInfoItem rateTerms={rateTerms} />
        {isCouponApplied && (
          <RateTermItem>
            {t(i18nKeys.rateTermsCoupon, { coupon: price.coupon?.code || '' })}
          </RateTermItem>
        )}
        <RateTermInfoItem rateTerms={rateTerms} isInfoMax />
        {/* // Todo: check and enable if both unlimitedFreeMiles and unlimitedMilage are needed */}
        {isUnlimitedMilage && (
          <RateTermItem>{t(i18nKeys.unlimitedMilesLabel)}</RateTermItem>
        )}
        {!isUnlimitedMilage && limitedMilesText && (
          <RateTermItem>{limitedMilesText}</RateTermItem>
        )}
        {!!rateTerms?.cancelFeeBefore24h && !payLater && (
          <RateTermItem>
            {t(i18nKeys.cancelFeeBefore24h, {
              earlyCancelFee: rateTerms?.cancelFeeBefore24h,
            })}
          </RateTermItem>
        )}
        {!!rateTerms?.cancelFeeWithin24h && !payLater && (
          <RateTermItem>
            {t(i18nKeys.cancelFeeWithin24h, {
              lateCancelFee: rateTerms?.cancelFeeWithin24h,
            })}
          </RateTermItem>
        )}
      </Box>
      {context?.showSpecialRateTerms && isUnlimitedMilage && (
        <AbgTypography sx={specialRateTermsNotesStyling}>
          <TickIcon sx={specialRateTermsIconStyling} />
          {t(i18nKeys.unlimitedMilesLabel)}
        </AbgTypography>
      )}
      {context?.showSpecialRateTerms &&
        !isUnlimitedMilage &&
        limitedMilesText && (
          <AbgTypography sx={specialRateTermsNotesStyling}>
            <TickIcon sx={specialRateTermsIconStyling} />
            {limitedMilesText}
          </AbgTypography>
        )}
      {context?.showSpecialRateTerms && isFreeCancellation && (
        <AbgTypography sx={specialRateTermsNotesStyling}>
          <TickIcon sx={specialRateTermsIconStyling} />
          {t(i18nKeys.freeCancellation)}
        </AbgTypography>
      )}
      {context?.showSpecialRateTerms && payLater && (
        <AbgTypography sx={specialRateTermsNotesStyling}>
          <TickIcon sx={specialRateTermsIconStyling} />
          {t(i18nKeys.zeroDueToday, {
            zeroamount: formatMoney(0, { currency, includeCents: false }),
          })}
        </AbgTypography>
      )}
      {context?.showSpecialRateTerms && !payLater && (
        <AbgTypography sx={specialRateTermsNotesStyling}>
          <TickIcon sx={specialRateTermsIconStyling} />
          {t(i18nKeys.payNowAndSave)}
        </AbgTypography>
      )}
      {context?.showSpecialRateTerms && !payLater && (
        <AbgTypography sx={specialRateTermsNotesStyling}>
          <TickIcon sx={specialRateTermsIconStyling} />
          {t(i18nKeys.greatDeal)}
        </AbgTypography>
      )}
    </>
  );
};
